import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/cn';
import type { BrowsableItem, BreadcrumbItem } from './types';

interface FolderPickerBreadcrumbsProps {
  currentFolder: BrowsableItem | 'root';
  workspaceName: string;
  onNavigateRoot: () => void;
  onNavigateFolder: (id: string) => void;
}

function buildCrumbs(
  currentFolder: BrowsableItem | 'root',
  workspaceName: string,
): BreadcrumbItem[] {
  const root: BreadcrumbItem = { title: workspaceName };

  if (currentFolder === 'root') return [root];

  const ancestors = currentFolder.breadcrumb ?? [];
  const current: BreadcrumbItem = {
    id: currentFolder.id,
    title: currentFolder.title,
    itemtype: currentFolder.itemtype,
  };

  const all = [root, ...ancestors, current];

  // Truncate long paths: show root + "..." + last 2
  if (all.length > 4) {
    return [all[0], { title: '...' }, all[all.length - 2], all[all.length - 1]];
  }

  return all;
}

export function FolderPickerBreadcrumbs({
  currentFolder,
  workspaceName,
  onNavigateRoot,
  onNavigateFolder,
}: FolderPickerBreadcrumbsProps) {
  const crumbs = buildCrumbs(currentFolder, workspaceName);

  return (
    <nav className="flex items-center gap-1 overflow-x-auto rounded-t-lg bg-gray-800 px-3 py-2">
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        const isEllipsis = crumb.title === '...';
        const isRoot = index === 0;

        const handleClick = () => {
          if (isLast || isEllipsis) return;
          if (isRoot) {
            onNavigateRoot();
          } else if (crumb.id) {
            onNavigateFolder(crumb.id);
          }
        };

        return (
          <span key={crumb.id ?? `crumb-${index}`} className="flex items-center gap-1">
            {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
            <button
              type="button"
              onClick={handleClick}
              disabled={isLast || isEllipsis}
              className={cn(
                'max-w-32 truncate whitespace-nowrap text-sm',
                isLast || isEllipsis
                  ? 'cursor-default text-muted-foreground'
                  : 'cursor-pointer text-white hover:text-primary',
              )}
              title={crumb.title}
            >
              {isRoot ? (
                <span className="flex items-center gap-1">
                  <Home className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{crumb.title}</span>
                </span>
              ) : (
                crumb.title
              )}
            </button>
          </span>
        );
      })}
    </nav>
  );
}
